import { inject, Injectable } from '@angular/core';
import { Observable, catchError, concat, map, of, throwError } from 'rxjs';
import {
  BulkUploadsService,
  type AppUploadError
} from 'src/app/infrastructure/services/bulk-loads.service';
import {
  UploadFileResponse,
  UploadIssueDetail,
  UploadResult
} from 'src/app/infrastructure/services/models/load.model';
import {
  UploadProcessResult,
  UploadIssue
} from '../../model/bulk-load/upload-result.model';
import { UPLOAD_CONSTANTS } from 'src/app/infrastructure/services/constants/upload.const';

@Injectable({
  providedIn: 'root'
})
export class BulkUploadsGateway {
  private readonly bulkUploadsService = inject(BulkUploadsService);

  generatePolicyNumber(operationType: string): Observable<string> {
    return this.bulkUploadsService.generatePolicyNumber(operationType);
  }

  processUpload(
    operationType: string,
    policyNumber: string,
    selectedFile: File | null
  ): Observable<UploadProcessResult> {
    return this.bulkUploadsService
      .processUpload(operationType, policyNumber, selectedFile)
      .pipe(
        map((result: UploadResult) =>
          this.mapUploadResponse(result.uploadResponse, '')
        ),
        catchError((error: unknown) => this.buildErrorResult(error, ''))
      );
  }

  retryUploadFile(
    selectedFile: File,
    loadId: string
  ): Observable<UploadProcessResult> {
    return this.bulkUploadsService.uploadFile(selectedFile, loadId).pipe(
      map((uploadResponse: UploadFileResponse) =>
        this.mapUploadResponse(uploadResponse, loadId)
      ),
      catchError((error: unknown) => this.buildErrorResult(error, loadId))
    );
  }

  downloadTemplateBase64(
    operationType: string,
    policyNumber: string
  ): Observable<string> {
    return this.bulkUploadsService.downloadTemplateBase64(
      operationType,
      policyNumber
    );
  }

  downloadFile(
    base64: string,
    fileName: string = UPLOAD_CONSTANTS.TEMPLATE_NAME
  ): void {
    this.bulkUploadsService.downloadFile(base64, fileName);
  }

  getTemplateFileName(): string {
    return UPLOAD_CONSTANTS.TEMPLATE_NAME;
  }

  private mapUploadResponse(
    uploadResponse: UploadFileResponse,
    fallbackLoadId: string
  ): UploadProcessResult {
    const issue: UploadIssue | null = uploadResponse.hasErrors
      ? this.mapInfrastructureIssueToDTO(
          this.bulkUploadsService.buildValidationIssue(uploadResponse)
        )
      : null;

    return {
      loadId: uploadResponse.loadId || fallbackLoadId,
      hasErrors: uploadResponse.hasErrors,
      issue
    };
  }

  private buildErrorResult(
    error: unknown,
    fallbackLoadId: string
  ): Observable<UploadProcessResult> {
    const technicalIssue = this.bulkUploadsService.buildTechnicalIssue(error);
    const resolvedLoadId =
      (error as Partial<AppUploadError>).generatedLoadId ?? fallbackLoadId;

    if (technicalIssue) {
      return concat(
        of({
          loadId: resolvedLoadId,
          hasErrors: true,
          issue: this.mapInfrastructureIssueToDTO(technicalIssue)
        }),
        throwError(() => error)
      );
    }

    return throwError(() => error);
  }

  private mapInfrastructureIssueToDTO(upload: UploadIssueDetail): UploadIssue {
    return {
      codes: upload.codes,
      fileBase64: upload.fileBase64 ?? null,
      fileName: upload.fileName ?? null
    };
  }
}
